import React, { useState, useEffect, useRef } from 'react';
import { FiPlay, FiPause, FiSquare, FiClock, FiPlus } from 'react-icons/fi';
import axios from 'axios';

const TaskTimer = ({ task, onTimeUpdate, className = "" }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const intervalRef = useRef(null);

  // Format time as HH:MM:SS
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Convert minutes to hours for display
  const formatHours = (minutes) => {
    if (!minutes) return '0h';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Start timer
  const startTimer = () => {
    const now = Date.now();
    setStartTime(now);
    setIsRunning(true);
    setElapsedTime(0);

    intervalRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - now) / 1000));
    }, 1000);
  };

  // Pause timer
  const pauseTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRunning(false);
  };

  // Stop timer and save time
  const stopTimer = async () => {
    pauseTimer();
    
    if (elapsedTime > 0) {
      try {
        const minutesToAdd = Math.ceil(elapsedTime / 60);
        const newActualHours = (task.actual_hours || 0) + minutesToAdd;
        
        // Update task with new actual hours and log activity
        await axios.put(`/api/planwise/tasks/${task.id}`, {
          actual_hours: newActualHours
        });

        // Add system comment for timer stop
        await axios.post(`/api/planwise/tasks/${task.id}/comments`, {
          content: `Timer stopped - ${Math.floor(minutesToAdd / 60)}h ${minutesToAdd % 60}m logged`,
          is_system: true,
          activity_type: 'time_logged'
        });

        // Notify parent component
        if (onTimeUpdate) {
          onTimeUpdate(task.id, newActualHours);
        }
      } catch (error) {
        console.error('Error saving time:', error);
      }
    }

    setElapsedTime(0);
    setStartTime(null);
  };

  // Add manual time entry
  const addManualTime = async () => {
    const hours = parseInt(manualHours) || 0;
    const minutes = parseInt(manualMinutes) || 0;
    const totalMinutes = (hours * 60) + minutes;
    
    if (totalMinutes <= 0) return;
    
    try {
      const newActualHours = (task.actual_hours || 0) + totalMinutes;
      
      // Update task with new actual hours
      await axios.put(`/api/planwise/tasks/${task.id}`, {
        actual_hours: newActualHours
      });

      // Add system comment for manual time entry
      await axios.post(`/api/planwise/tasks/${task.id}/comments`, {
        content: `Manual time entry - ${hours}h ${minutes}m added`,
        is_system: true,
        activity_type: 'time_added'
      });

      // Notify parent component
      if (onTimeUpdate) {
        onTimeUpdate(task.id, newActualHours);
      }

      // Reset manual entry form
      setManualHours('');
      setManualMinutes('');
      setShowManualEntry(false);
    } catch (error) {
      console.error('Error adding manual time:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      {/* Timer Display */}
      <div className="flex items-center gap-2 text-sm">
        <FiClock className="w-4 h-4 text-gray-500" />
        <div className="flex flex-col">
          {/* Current Session */}
          {isRunning && (
            <div className="text-blue-600 dark:text-blue-400 font-mono font-medium">
              {formatTime(elapsedTime)}
            </div>
          )}
          
          {/* Total Time */}
          <div className="text-gray-600 dark:text-gray-400 text-xs">
            {task.actual_hours ? formatHours(task.actual_hours) : '0h'} logged
          </div>
        </div>
      </div>

      {/* Timer Controls */}
      <div className="flex items-center gap-1">
        {!isRunning ? (
          <>
            <button
              onClick={startTimer}
              className="p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors group"
              title="Start timer"
            >
              <FiPlay className="w-4 h-4 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" />
            </button>
            <button
              onClick={() => setShowManualEntry(!showManualEntry)}
              className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors group"
              title="Add time manually"
            >
              <FiPlus className="w-4 h-4 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={pauseTimer}
              className="p-1 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded transition-colors group"
              title="Pause timer"
            >
              <FiPause className="w-4 h-4 text-yellow-600 dark:text-yellow-400 group-hover:text-yellow-700 dark:group-hover:text-yellow-300" />
            </button>
            <button
              onClick={stopTimer}
              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors group"
              title="Stop and save time"
            >
              <FiSquare className="w-4 h-4 text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300" />
            </button>
          </>
        )}
      </div>

      {/* Manual Time Entry Overlay */}
      {showManualEntry && (
        <div className="absolute top-full right-0 mt-1 w-56 p-2.5 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600 shadow-lg z-50">
          <div className="mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add Time:</span>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={manualHours}
                  onChange={(e) => setManualHours(e.target.value)}
                  placeholder="0"
                  min="0"
                  max="24"
                  className="w-12 px-1 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">h</span>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={manualMinutes}
                  onChange={(e) => setManualMinutes(e.target.value)}
                  placeholder="0"
                  min="0"
                  max="59"
                  className="w-12 px-1 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">m</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={addManualTime}
                disabled={!manualHours && !manualMinutes}
                className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded transition-colors"
              >
                Add
              </button>
              <button
                onClick={() => setShowManualEntry(false)}
                className="flex-1 px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTimer;
